import { HandBack, type LogRow, type Row } from '../janus-engine/ability/context.js';
import { hasSurfaceTypeValue, paoeEntryTime, paoeEntryTurn, PAOE_ENTRY_RECORD, surfaceEntrySkipStamp, type PaoeEntryFact, type PaoeEntryRecords } from '../janus-engine/ability/paoe-entry-once.js';
import { abilityTruthCampaign, abilityTruthReaders } from './ability-truth.js';
import { encounterField } from '../shared/array-types.js';
import { zoneRollIsAdverse } from '../janus-engine/zones/end-of-turn-zones.js';
import { ftCampaignClockIsRunning } from '../shared/campaign-clock-transport.js';

const held = new Map<string, PaoeEntryRecords>();
const flights = new Map<string, Promise<PaoeEntryRecords>>();
const generations = new Map<string, number>();
const keyOf = (encounter: Row): string => String(encounterField(encounter, 'encounter_id') ?? '').toUpperCase();
const turnOf = (encounter: Row): string => paoeEntryTurn(encounter, abilityTruthReaders({}, encounter, new Set()).campaignClock());
const object = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
type HeldEntryFact = PaoeEntryFact & { confirmed?: true; operations?: string[] };
const surfaceLog = new Map<string, Map<string, HeldEntryFact[]>>();
const SURFACE_LOG_KEY = 'ft.surface-entry-log.v1:';
const corrected = new Map<string, Set<string>>();
const logReceived = new Set<string>();
const campaignLogReceived = new Set<string>();
const logRequested = new Set<string>();
// log_display_inc.php delivers a newest-first, campaign-wide window capped at 50 rows.
// Entry facts alone cannot show whether an earlier check fell off that window.
interface DeliveredSurfaceScope { turn: string; time: number[]; surfaces: string[]; creatures: string[] }
const requestedSurfaceScopes = new Map<string, Map<string, DeliveredSurfaceScope>>();
interface SurfaceLogWindow { full: boolean; oldestTime: number[] | null; turnStarts: Set<string>; delivered: Map<string, DeliveredSurfaceScope> }
const logWindows = new Map<string, SurfaceLogWindow>();
// A live boundary may beat the initial log response. Later window delivery must retain that proof.
const knownTurnStarts = new Set<string>();
const campaignIdOf = (encounter: Row): string => {
  const campaign = object(encounter.campaign_arr) ?? abilityTruthCampaign(encounter);
  return String(object(campaign?.campaigns)?.campaign_id ?? campaign?.campaign_id ?? encounterField(encounter, 'campaign_id') ?? '').toUpperCase();
};

function logWindowTime(value: unknown): number[] | null {
  return Array.isArray(value) && value.length === 6 && value.every(part =>
    (typeof part === 'number' || typeof part === 'string' && part.trim() !== '') && Number.isSafeInteger(Number(part)) && Number(part) >= 0)
    ? value.map(Number) : null;
}

function logRowTime(row: Row | null): number[] | null {
  return logWindowTime(['year', 'month', 'day', 'hour', 'minute', 'second'].map(unit => row?.[`roll_time_${unit}`]));
}

function surfaceWindow(id: string, campaignId: string): SurfaceLogWindow | undefined {
  return (campaignId ? logWindows.get(`campaign:${campaignId}`) : undefined) ?? logWindows.get(`encounter:${id}`);
}

function clockAdvanced(before: readonly number[], after: readonly number[]): boolean {
  const difference = before.findIndex((part, index) => part !== after[index]);
  return difference >= 0 && before[difference]! < after[difference]!;
}

/** Freeze identities and the held turn synchronously when the ordinary log is requested. */
function deliveredSurfaceScope(encounter: Row): DeliveredSurfaceScope {
  const read = abilityTruthReaders({}, encounter, new Set());
  const creatures = Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : [];
  const inventories = Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr : [];
  const surfaces = new Set<string>();
  for (const value of [encounter, ...creatures, ...inventories]) {
    const statuses = object(object(value)?.statuses)?.target;
    if (!Array.isArray(statuses)) continue;
    for (const value of statuses) {
      const status = object(value);
      if (!status || Number(status.status_aoe_source) !== 1) continue;
      const definition = read.definition(String(status.status_origin_id || status.status_item_id || '').toUpperCase());
      const connection = String(status.status_connected_id ?? '').toUpperCase();
      if (connection && hasSurfaceTypeValue(read, definition)) surfaces.add(connection);
    }
  }
  return { turn: turnOf(encounter), time: paoeEntryTime(read.campaignClock()), surfaces: [...surfaces],
    creatures: creatures.map(value => String(object(object(value)?.encounter_creatures)?.encounter_creature_id
      ?? object(value)?.encounter_creature_id ?? '').toUpperCase()).filter(Boolean) };
}

/** Only identities that existed in the delivered turn can have checks outside its truncated log. */
function surfaceHistoryIncomplete(encounter: Row, id: string, campaignId: string, turn: string, time: readonly number[]): DeliveredSurfaceScope | undefined {
  const window = surfaceWindow(id, campaignId), scope = window?.delivered.get(id);
  if (!window?.full || !scope || !scope.surfaces.length || !scope.creatures.length) return undefined;
  if (window.oldestTime) {
    if (clockAdvanced(window.oldestTime, time)) return undefined;
  }
  if (Number(encounterField(encounter, 'in_combat') ?? 0) !== 1) return undefined;
  // A held turn-start row proves that the window contains everything since that boundary.
  // Other encounters' boundaries do not establish coverage for this encounter.
  if (window.turnStarts.has(id)) return undefined;
  return scope.turn === turn ? scope : undefined;
}

function correctedSurfaceOperations(encounterId: string): Set<string> {
  let operations = corrected.get(encounterId);
  if (operations) return operations;
  operations = new Set();
  try {
    const saved: unknown = JSON.parse(sessionStorage.getItem(SURFACE_LOG_KEY + encounterId + ':corrections') ?? 'null');
    if (Array.isArray(saved)) for (const operation of saved) if (typeof operation === 'string') operations.add(operation);
  } catch { /* optional browser durability */ }
  corrected.set(encounterId, operations);
  return operations;
}

/** A compact projection of delivered entry markers and adverse-roll identities, retained beyond the visible log window. */
function surfaceTurns(encounterId: string): Map<string, HeldEntryFact[]> {
  const id = encounterId.toUpperCase();
  let turns = surfaceLog.get(id);
  if (turns) return turns;
  turns = new Map();
  try {
    const saved: unknown = JSON.parse(sessionStorage.getItem(SURFACE_LOG_KEY + id) ?? 'null');
    if (Array.isArray(saved)) for (const entry of saved) {
      if (Array.isArray(entry) && typeof entry[0] === 'string' && Array.isArray(entry[1])) {
        const facts = entry[1].filter((row: unknown) => surfaceEntrySkipStamp(object(row) as PaoeEntryFact | null) !== null || (object(row)?.roll_type_1 === PAOE_ENTRY_RECORD
          ? typeof object(row)?.rolls_full === 'string' : zoneRollIsAdverse(String(object(row)?.roll_type_1 ?? '').toUpperCase())
            && Array.isArray(object(row)?.time) && (object(row)!.time as unknown[]).length === 6)) as HeldEntryFact[];
        turns.set(entry[0], facts.map(fact => Array.isArray(fact.operations)
          ? { ...fact, operations: fact.operations.filter(operation => typeof operation === 'string') }
          : { ...fact, confirmed: true }));
      }
    }
  } catch { /* Storage can be unavailable; already held and delivered log rows still work. */ }
  surfaceLog.set(id, turns);
  return turns;
}

/** Pure projection input: normal log JSON, a live roll_insert, or a successfully served entry commit. No history fetch. */
export function rememberPaoeEntryLogRows(rows: readonly unknown[], encounterId = '', operationId = '', sourceTime?: unknown): void {
  operationId = operationId.toUpperCase();
  const sourceClock = Array.isArray(sourceTime) && sourceTime.length === 6
    && sourceTime.every(part => Number.isSafeInteger(part) && part >= 0) ? sourceTime as number[] : undefined;
  const changed = new Set<string>();
  // The normal log window arrives newest first; retain its newest turn/time windows when trimming the compact index.
  for (const value of [...rows].reverse()) {
    const row = object(value);
    if (!row) continue;
    if (row.roll_type_1 === 'start_turn' || row.roll_type_1 === 'start_legendary_turn') {
      const id = String(row.encounter_id ?? encounterId).toUpperCase();
      if (id) {
        knownTurnStarts.add(id);
        for (const window of logWindows.values()) window.turnStarts.add(id);
      }
    }
    const marker = row.roll_type_1 === PAOE_ENTRY_RECORD;
    const skipStamp = surfaceEntrySkipStamp(row as PaoeEntryFact);
    if (!marker && !skipStamp && !zoneRollIsAdverse(String(row.roll_type_1 ?? '').toUpperCase())) continue;
    const id = String(row.encounter_id ?? encounterId).toUpperCase();
    if (!id) continue;
    if (operationId && correctedSurfaceOperations(id).has(operationId)) continue;
    const stamp = skipStamp ?? (typeof row.rolls_full === 'string' ? row.rolls_full : '');
    const cut = stamp.lastIndexOf('|');
    const time = ['year', 'month', 'day', 'hour', 'minute', 'second'].map((unit, index) => row[`roll_time_${unit}`] ?? sourceClock?.[index]);
    if (marker || skipStamp ? cut < 0 : time.some(value => value === undefined || value === null || !Number.isFinite(Number(value)))) continue;
    const turn = marker || skipStamp ? stamp.slice(0, cut) : JSON.stringify(['time', ...time.map(Number)]);
    const turns = surfaceTurns(id), prior = turns.get(turn) ?? [];
    const fact: HeldEntryFact = { roll_type_1: String(row.roll_type_1), roll_item_id: String(row.roll_item_id ?? '').toUpperCase(),
      roll_by: String(row.roll_by ?? '').toUpperCase(), roll_to: String(row.roll_to ?? '').toUpperCase(),
      ...(marker || skipStamp ? { rolls_full: row.rolls_full as LogRow['rolls_full'] } : { time: time.map(Number) }),
      ...(skipStamp ? { roll_type_2: 'history-incomplete' } : {}),
      ...(operationId ? { operations: [operationId] } : { confirmed: true }) };
    const existing = prior.find(existing => (skipStamp ? surfaceEntrySkipStamp(existing) === skipStamp : existing.rolls_full === fact.rolls_full) && existing.roll_item_id === fact.roll_item_id
      && existing.roll_type_1 === fact.roll_type_1 && existing.roll_by === fact.roll_by && existing.roll_to === fact.roll_to);
    if (!existing) {
      prior.push(fact); turns.set(turn, prior); changed.add(id);
    } else if (!operationId && !existing.confirmed) {
      existing.confirmed = true; delete existing.operations; changed.add(id);
    } else if (operationId && !existing.confirmed && !existing.operations?.includes(operationId)) {
      (existing.operations ??= []).push(operationId); changed.add(id);
    }
  }
  for (const id of changed) try { sessionStorage.setItem(SURFACE_LOG_KEY + id, JSON.stringify([...surfaceTurns(id)])); } catch { /* optional durable browser storage */ }
}

/** Retire only the corrected operation's projected facts, preserving independent successful delivery of the same marker. */
function correctSurfaceEntryLog(encounterId: string, operationId: string): void {
  const id = encounterId.toUpperCase(), operation = operationId.toUpperCase(), turns = surfaceTurns(id);
  if (!operation) return;
  const corrections = correctedSurfaceOperations(id);
  corrections.add(operation);
  while (corrections.size > 512) corrections.delete(corrections.values().next().value!);
  try { sessionStorage.setItem(SURFACE_LOG_KEY + id + ':corrections', JSON.stringify([...corrections])); } catch { /* held tombstone still applies */ }
  let changed = false;
  for (const [turn, facts] of turns) {
    const kept = facts.filter(fact => {
      if (fact.confirmed || !fact.operations?.includes(operation)) return true;
      fact.operations = fact.operations.filter(candidate => candidate !== operation); changed = true;
      return fact.operations.length > 0;
    });
    if (kept.length) turns.set(turn, kept); else turns.delete(turn);
  }
  if (changed) try { sessionStorage.setItem(SURFACE_LOG_KEY + id, JSON.stringify([...turns])); } catch { /* held correction still applies */ }
}

/** A zone takes the surface memo path only when its held surface_type value is non-empty. */
export function onlySurfaceEntryZones(encounter: Row): boolean {
  const read = abilityTruthReaders({}, encounter, new Set());
  const holders = [encounter, ...(Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []),
    ...(Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr : [])];
  const anchors = holders.flatMap(holder => {
    const statuses = object(object(holder)?.statuses)?.target;
    return Array.isArray(statuses) ? statuses.flatMap(value => {
      const status = object(value);
      return status && Number(status.status_aoe_source) === 1 ? [status] : [];
    }) : [];
  });
  return anchors.length > 0 && anchors.every(anchor => {
    const definition = read.definition(String(anchor.status_origin_id || anchor.status_item_id || '').toUpperCase());
    return hasSurfaceTypeValue(read, definition);
  });
}

export function readSurfaceEntryRecords(encounter: Row): PaoeEntryRecords {
  const id = keyOf(encounter), campaignId = campaignIdOf(encounter), turn = turnOf(encounter), turns = surfaceTurns(id);
  const clock = paoeEntryTime(abilityTruthReaders({}, encounter, new Set()).campaignClock());
  const time = JSON.stringify(['time', ...clock]);
  const delivered = surfaceWindow(id, campaignId)?.delivered.get(id);
  // A stopped/missing clock cannot turn one move's budget into a permanent prohibition.
  // Within a move, the resolver carries its own markers between crossings and contexts.
  const moveOnly = Number(encounterField(encounter, 'in_combat') ?? 0) !== 1
    && (!delivered || !clockAdvanced(delivered.time, clock) || !ftCampaignClockIsRunning());
  const incomplete = surfaceHistoryIncomplete(encounter, id, campaignId, turn, clock);
  // A delayed initial log can deliver old turns after a live marker. Trim only when the held encounter
  // identifies the current budget; arrival order must never evict that turn or its adverse-roll window.
  const markerWindows: string[] = [], clockWindows: string[] = [];
  for (const [key, facts] of turns) {
    if (key !== turn && key !== time) (facts.some(fact => fact.roll_type_1 === PAOE_ENTRY_RECORD || surfaceEntrySkipStamp(fact)) ? markerWindows : clockWindows).push(key);
  }
  let trimmed = false;
  for (const windows of [markerWindows, clockWindows]) while (windows.length > 3) {
    turns.delete(windows.shift()!); trimmed = true;
  }
  if (trimmed) try { sessionStorage.setItem(SURFACE_LOG_KEY + id, JSON.stringify([...turns])); } catch { /* held pruning still applies */ }
  return { turn, rows: [...turns.get(turn) ?? [], ...(time === turn ? [] : turns.get(time) ?? [])], surfaceOnly: true,
    ...(moveOnly ? { surfaceEntryMoveOnly: true as const } : {}),
    ...(incomplete ? { surfaceEntryHistoryIncomplete: true as const,
      surfaceEntryUncertain: { surfaces: incomplete.surfaces, creatures: incomplete.creatures } } : {}),
    ...(incomplete || (!logReceived.has(id) && !(campaignId && campaignLogReceived.has(campaignId)))
      ? { surface_entry_log_receipt: 'no-prior-check-this-turn' as const } : {}) };
}

/** Kick off the ordinary campaign log once. A walk uses held facts immediately while it loads or if it fails. */
function ensureSurfaceLog(encounter: Row): null {
  const id = keyOf(encounter), campaignId = campaignIdOf(encounter);
  if (logReceived.has(id) || (campaignId && campaignLogReceived.has(campaignId))) return null;
  const key = campaignId ? `campaign:${campaignId}` : `encounter:${id}`;
  if (logRequested.has(key)) return null;
  const load = (window as Window & { buildLog?: (context: { campaignId?: string; encounterId: string; memoOnly: true }) => Promise<unknown> }).buildLog;
  if (typeof load === 'function') {
    logRequested.add(key);
    try { void Promise.resolve(load({ campaignId, encounterId: id, memoOnly: true })).catch(() => { /* receipt records the unavailable window */ }); }
    catch { /* receipt records the unavailable window */ }
  }
  return null;
}

export function ensurePaoeEntryRecords(encounter: Row, actorId: string, surfaceOnly = false): Promise<PaoeEntryRecords> | null {
  if (surfaceOnly) return ensureSurfaceLog(encounter);
  const records = held.get(keyOf(encounter));
  return records?.turn === turnOf(encounter) ? null : hydratePaoeEntryRecords(encounter, actorId);
}

export function readPaoeEntryRecords(encounter: Row): PaoeEntryRecords {
  const records = held.get(keyOf(encounter));
  if (!records || records.turn !== turnOf(encounter)) throw new HandBack('paoe-entry:turn-records-not-held');
  return records;
}

export async function hydratePaoeEntryRecords(encounter: Row, actorId: string, surfaceOnly = false): Promise<PaoeEntryRecords> {
  if (surfaceOnly) { ensureSurfaceLog(encounter); return readSurfaceEntryRecords(encounter); }
  // A peer's marker can invalidate a read already in flight. Re-read the new generation once.
  for (let attempt = 0; ; attempt++) {
    try { return await fetchPaoeEntryRecords(encounter, actorId); }
    catch (error) { if (attempt !== 0 || !(error instanceof HandBack) || error.reason !== 'paoe-entry:turn-records-stale') throw error; }
  }
}

async function fetchPaoeEntryRecords(encounter: Row, actorId: string): Promise<PaoeEntryRecords> {
  const id = keyOf(encounter), turn = turnOf(encounter), generation = generations.get(id) ?? 0, key = `${id}|${turn}|${generation}`;
  let flight = flights.get(key);
  if (!flight) {
    flight = (async () => {
      const time = paoeEntryTime(abilityTruthReaders({}, encounter, new Set()).campaignClock());
      const response = await fetch(BASE_URL + 'options/game/rolls/teleport_offer_inc.php', { method: 'POST',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mode: 'entry-records', caster_id: actorId, encounter_id: id, prefix: `${turn}|`,
          time }),
        signal: AbortSignal.timeout(12000) });
      const result = await response.json() as { ok?: boolean; rows?: PaoeEntryFact[] };
      if (!response.ok || !result.ok || !Array.isArray(result.rows)) throw new HandBack('paoe-entry:turn-records-unavailable');
      if ((generations.get(id) ?? 0) !== generation || turnOf(encounter) !== turn) throw new HandBack('paoe-entry:turn-records-stale');
      const prior = held.get(id);
      const records = { turn, rows: [...result.rows, ...(prior?.turn === turn ? prior.rows : [])] };
      held.set(id, records);
      return records;
    })().finally(() => flights.delete(key));
    flights.set(key, flight);
  }
  return flight;
}

export function adoptPaoeEntryRecords(encounter: Row, rows: readonly LogRow[]): void {
  rememberPaoeEntryLogRows(rows, keyOf(encounter));
  const prior = held.get(keyOf(encounter)), added = rows.filter(row => row.roll_type_1 === PAOE_ENTRY_RECORD);
  if (prior && prior.turn === turnOf(encounter)) held.set(keyOf(encounter), { ...prior, rows: [...prior.rows, ...added] });
}

export function forgetPaoeEntryRecords(encounter: Row): void {
  invalidatePaoeEntryRecords(keyOf(encounter));
}

export function invalidatePaoeEntryRecords(encounterId: string): void {
  const id = encounterId.toUpperCase();
  held.delete(id); generations.set(id, (generations.get(id) ?? 0) + 1);
}

window.addEventListener('ft:log-rows-requested', event => {
  const detail = object((event as CustomEvent).detail), scopes = object(detail?.surfaceEntryScopes);
  if (!scopes) return;
  const id = String(detail?.encounterId ?? '').toUpperCase(), campaignId = String(detail?.campaignId ?? '').toUpperCase();
  const key = campaignId ? `campaign:${campaignId}` : `encounter:${id}`;
  logRequested.add(key);
  let initial = requestedSurfaceScopes.get(key);
  if (!initial) {
    initial = new Map();
    for (const value of window.FateTide?.encounterCache?.values() ?? []) {
      const encounter = object(value);
      if (encounter && (campaignId ? campaignIdOf(encounter) === campaignId : keyOf(encounter) === id))
        initial.set(keyOf(encounter), deliveredSurfaceScope(encounter));
    }
    // Overlapping refreshes may return out of order. The first request owns the uncertainty epoch.
    requestedSurfaceScopes.set(key, initial);
  }
  for (const [encounterId, scope] of initial) scopes[encounterId] = scope;
});

window.addEventListener('ft:log-rows-received', event => {
  const detail = object((event as CustomEvent).detail);
  if (Array.isArray(detail?.rows)) {
    const id = String(detail.encounterId ?? '').toUpperCase();
    const campaignId = String(detail.campaignId ?? '').toUpperCase();
    rememberPaoeEntryLogRows(detail.rows, id);
    const turnStarts = new Set(knownTurnStarts);
    for (const value of detail.rows) {
      const row = object(value);
      if (row?.roll_type_1 === 'start_turn' || row?.roll_type_1 === 'start_legendary_turn') {
        const rowEncounterId = String(row.encounter_id ?? id).toUpperCase();
        if (rowEncounterId) turnStarts.add(rowEncounterId);
      }
    }
    const rawWindow = object(detail.logWindow), hasRawWindow = typeof rawWindow?.full === 'boolean';
    const key = campaignId ? `campaign:${campaignId}` : `encounter:${id}`, previousWindow = logWindows.get(key);
    const delivered = new Map(previousWindow?.delivered);
    const requestedScopes = object(detail.surfaceEntryScopes);
    for (const value of window.FateTide?.encounterCache?.values() ?? []) {
      const encounter = object(value);
      if (encounter && (campaignId ? campaignIdOf(encounter) === campaignId : keyOf(encounter) === id)) {
        // Refreshing the visible log cannot make a newer surface/creature historically uncertain.
        // Retain its original clock too: a log refresh cannot reset an advancing clock's budget.
        const requested = requestedScopes?.[keyOf(encounter)] as DeliveredSurfaceScope | undefined;
        const scope = previousWindow?.delivered.get(keyOf(encounter)) ?? requested
          ?? (requestedScopes ? undefined : deliveredSurfaceScope(encounter));
        if (scope) delivered.set(keyOf(encounter), scope);
      }
    }
    let oldestTime = hasRawWindow ? logWindowTime(rawWindow?.oldest_time) : logRowTime(object(detail.rows[detail.rows.length - 1]));
    if (previousWindow?.oldestTime && (!oldestTime || clockAdvanced(previousWindow.oldestTime, oldestTime)))
      oldestTime = previousWindow.oldestTime;
    logWindows.set(key, {
      full: (hasRawWindow ? rawWindow?.full === true : detail.rows.length >= 50) && previousWindow?.full !== false,
      oldestTime,
      turnStarts, delivered,
    });
    if (id) logReceived.add(id);
    if (campaignId) campaignLogReceived.add(campaignId);
  }
});

// Truth frames contain the unredacted log facts even when the presentation roll_insert does not. Their operation identity
// follows a publish-first frame through correction; only ordinary saved log delivery upgrades it to a confirmed fact.
window.addEventListener('ft:ability-truth-frame', event => {
  const frame = object((event as CustomEvent).detail), verdict = object(frame?.verdict);
  if (frame?.committed === true && frame.correction !== true && typeof frame.operation_id === 'string' && Array.isArray(verdict?.rows))
    rememberPaoeEntryLogRows(verdict.rows, String(frame.encounter_id ?? ''), frame.operation_id, verdict.surface_entry_time);
});
window.addEventListener('ft:status-truth-frame', event => {
  const detail = object((event as CustomEvent).detail), command = object(detail?.command), verdict = object(command?.gm_boundary);
  if (detail?.kind === 'committed' && command?.committed === true && command.correction !== true
    && typeof command.operation_id === 'string' && Array.isArray(verdict?.rows))
    rememberPaoeEntryLogRows(verdict.rows, String(command.encounter_id ?? ''), command.operation_id, verdict.surface_entry_time);
});
window.addEventListener('ft:ability-truth-applied', event => {
  const detail = object((event as CustomEvent).detail), command = object(detail?.command), verdict = object(command?.verdict);
  if (command && command.correction !== true && typeof command.operation_id === 'string' && Array.isArray(verdict?.rows))
    rememberPaoeEntryLogRows(verdict.rows, String(command.encounter_id ?? ''), command.operation_id, verdict.surface_entry_time);
});
window.addEventListener('ft:truth-correction', event => {
  const detail = object((event as CustomEvent).detail);
  if (typeof detail?.encounterId === 'string' && typeof detail.operationId === 'string')
    correctSurfaceEntryLog(detail.encounterId, detail.operationId);
});
